import classNames from 'classnames';
import Icon from 'components/shared/icon';
import styles from './progressBar.module.scss';

// One milestone circle for every marker type on the bar (tier percentage,
// gift, shipping). The ring, fill animation and positioning are shared;
// only the content differs.
const Milestone = ({ position, reached, color, className, children }) => (
  <span
    className={classNames(styles.milestone, className, { [styles.complete]: reached })}
    // Centered on its position but clamped so the circle stays flush inside
    // the bar ends (1.7rem = half the 3.4rem circle width)
    style={{ left: `clamp(1.7rem, ${Math.min(position, 100)}%, calc(100% - 1.7rem))` }}
  >
    <span className={styles.milestoneFill} style={{ backgroundColor: color }} />
    {children}
  </span>
);

const ProgressBar = ({
  current = 0,
  max = 100,
  className,
  color,
  showGiftIcon,
  sections = 0,
  tiers = [],
  giftThresholds = [],
  shippingThreshold,
}) => {
  const calcReadyPercentage = () => {
    const readyPercentage = (parseFloat(current) / parseFloat(max)) * 100;
    if (readyPercentage > 100) return 100;
    return readyPercentage;
  };

  const isComplete = parseFloat(current) >= parseFloat(max);

  const renderTierMarkers = () => {
    if (sections <= 1) return null;
    return Array.from({ length: sections }, (_, idx) => idx + 1).map(step => {
      const tier = tiers?.find(t => t.minimumQuantity === step);
      if (!tier?.discountPercentage) return null;
      const isReached = current >= tier.minimumQuantity;
      return (
        <Milestone key={step} position={(step / sections) * 100} reached={isReached} color={color}>
          <span className={styles.milestoneLabel}>
            {Math.round(tier.discountPercentage * 100)}%
          </span>
        </Milestone>
      );
    });
  };

  return (
    <div className={classNames(styles.wrapper, className)}>
      <div className={styles.container}>
        <div
          className={styles.progress}
          style={{
            width: `${calcReadyPercentage()}%`,
            backgroundColor: color,
          }}
        />
        {sections > 1 && <div className={styles.markersOverlay}>{renderTierMarkers()}</div>}
        {showGiftIcon && !giftThresholds.length && (
          <Milestone position={100} reached={isComplete} color={color}>
            <Icon type="gift" fill={isComplete ? '#fff' : '#bababa'} />
          </Milestone>
        )}
        {giftThresholds.map(threshold => {
          const isReached = parseFloat(current) >= threshold.value;
          return (
            <Milestone
              key={threshold.discountId}
              position={(threshold.value / parseFloat(max)) * 100}
              reached={isReached}
              color={color}
            >
              <Icon type="gift" fill={isReached ? '#fff' : '#bababa'} />
            </Milestone>
          );
        })}
        {shippingThreshold ? (
          <Milestone
            position={(shippingThreshold / parseFloat(max)) * 100}
            reached={parseFloat(current) >= shippingThreshold}
            color={color}
            className={styles.shippingMilestone}
          >
            <Icon
              type="shipping"
              fill={parseFloat(current) >= shippingThreshold ? '#fff' : '#bababa'}
            />
          </Milestone>
        ) : null}
      </div>
    </div>
  );
};

export default ProgressBar;
